// Design-iteration switches, read from the page's query string so one link can
// show Gavin a variant and another link its rival. Temporary by nature: once
// he picks, the winner becomes the only option and this file goes.
//
//   ?compass=v5 | v6 | v6-outline | v7 | v8 | v9 | v10 | v10-hard-edges
//           | cone                                    (default cone)
//   ?compass=<Gavin's attachment name>                (see GAVIN_FILE_NAMES)
//   ?playbar=fill | track                             (default fill)
//   ?radar=mono | colour                              (default mono)
//   ?verifylogo=v5 | v4-a | v4-b | v3 | logo | logo-v1     (default v5)
//   ?viewbutton=v4 | v3                               (default v4)
//
// They combine: `?compass=v9&playbar=fill`, or equally
// `?compass=Radar-Green-Blue-Clear-Pointer.png&playbar=fill`.

export type CompassVariantKey =
  'v5' | 'v6' | 'v6-outline' | 'v7' | 'v8' | 'v9' | 'v10' | 'v10-hard-edges'
  | 'cone'

export interface CompassVariant {
  image: string
  // The radar discs turn with the view, so the part of the artwork that means
  // "where you are looking" follows the camera. v5 is a fixed ring and the
  // chevron does the turning instead.
  rotates: boolean
  // Degrees clockwise to turn the artwork so its "where you are looking" mark
  // sits at 12 o'clock when yaw is 0. v8 and v9 are drawn with their dark wedge
  // spanning 286°–360° — 74°, near enough the camera's 75° field of view — so
  // turning them 37° centres that wedge on the heading and stands the V upright.
  // v10 draws the cone as a see-through gap instead, measured at 353.5°–63.5°
  // (the hard-edged cut 351°–66°): both centred on 28.5°, so they turn back by
  // that much.
  offset: number
  // The CSS cone (Gavin, 16 Sep): no disc and no glass plate, just a filled
  // wedge drawn live, so it can NARROW as the viewer zooms in — which a PNG
  // cannot do. `image` is empty because it draws itself; see ViewCone.tsx.
  cone?: true
}

export const COMPASS_VARIANTS: Record<CompassVariantKey, CompassVariant> = {
  v5: { image: '/img/radar-v5.png', rotates: false, offset: 0 },
  v6: { image: '/img/radar-v6-no-outline.png', rotates: true, offset: 0 },
  'v6-outline': { image: '/img/radar-v6-outline.png', rotates: true, offset: 0 },
  v7: { image: '/img/radar-v7-green-blue.png', rotates: true, offset: 0 },
  v8: { image: '/img/radar-v8-pointer-line.png', rotates: true, offset: 37 },
  v9: { image: '/img/radar-v9-pointer-clear.png', rotates: true, offset: 37 },
  v10: { image: '/img/radar-v10.png', rotates: true, offset: -28.5 },
  'v10-hard-edges': { image: '/img/radar-v10-hard-edges.png', rotates: true, offset: -28.5 },
  cone: { image: '', rotates: true, offset: 0, cone: true },
}

// The same designs by the names Gavin gave them — his email attachments of
// 10 and 11 Sep — so a conversation about "Radar-outline.png" needs no
// translating. Matched case-insensitively, with or without the `.png`. v5
// predates his emails, so it has no entry.
export const GAVIN_FILE_NAMES: Record<string, CompassVariantKey> = {
  'radar.png': 'v6',
  'radar-outline.png': 'v6-outline',
  'radar-green-blue.png': 'v7',
  'radar-green-blue-pointer.png': 'v8',
  'radar-green-blue-clear-pointer.png': 'v9',
  'radar-gradient.png': 'v10',
  'radar-gradient-hard-edges.png': 'v10-hard-edges',
}

// The 11 Sep review settled on a pure green-to-blue gradient with no line,
// the cone defined by the gradient alone — which is v10. (v8 was the default
// before that, from Gavin's 10 Sep recap.)
// Kai, 16 Sep: the CSS cone is the default, so nobody needs a query string to
// see it. The discs stay reachable with ?compass=v10 and friends.
const DEFAULT_COMPASS: CompassVariantKey = 'cone'

export type PlaybarVariant = 'track' | 'fill'

// Sampled from the ends of Gavin's swatch (ref-playbar-green-to-blue.png).
export const PLAYBAR_GREEN = '#007D51'
export const PLAYBAR_BLUE = '#00ADB3'

// The cone's ramp, read off Gavin's 16 Sep mock pixel by pixel: a near-pure
// green at the wide outer end running to a bright cyan at the apex. It is a
// WIDER run than the play bar, whose own green sits inside it — so the two
// read as one family without being the same two colours.
//
// It is RADIAL, spreading from the apex, not a straight run down the cone.
// Measured: on one scanline of the mock the cone's edge is visibly greener
// than its middle, and the edge is the further from the apex of the two. So
// the colour tracks DISTANCE from the viewer, which is also the honest
// reading — a band of one colour is a ring of equal range.
export const CONE_APEX = '#00A9DB'
export const CONE_MID = '#009984'
export const CONE_OUTER = '#007F12'

// Where those three sit, as a fraction of the avatars' ORBIT RADIUS — the
// distance itself, not a fraction of the cone. Fitted to every cone pixel of
// the mock binned by its distance from the apex: cyan holds flat out to 0.37,
// then runs down to a near-pure green by 0.93.
//
// Measuring against the dial rather than the cone is what makes the colour
// mean something once the cone starts moving with the zoom: a shade is a
// RANGE. The cone's corners are pinned to the orbit at every width, so the
// full run is always on show along its edges however far it opens.
//
// Gavin, 30 Sep, on the fading cone: "move the green closer to the edge of
// the wedge, so it's more like a green border with a bit of a gradient into
// the blue". So the cyan now holds most of the way out and the whole run to
// green happens in the last quarter. (The 16 Sep fit was 0.37 / 0.65 / 0.93.)
export const CONE_APEX_HOLD = 0.72
export const CONE_MID_AT = 0.86
export const CONE_OUTER_AT = 0.97

// Gavin, 16 Oct: "Can we have a look at the radar as monochrome — the rest
// stays as green". Read off his two mocks of that day, one over black and one
// over sky. The disc is white building from nothing at the viewer to about
// half at the rim — the sky mock shows the centre clear through. The wedge is
// a mid grey on top of it, lighter going out and darkening a little right at
// the rim, so its far edge still reads. The mask in ViewCone keeps the centre
// see-through, as in colour. Values solved from the black mock's pixels, net
// of the disc showing through the wedge's mask.
export const CONE_MONO_GRADIENT =
  'radial-gradient(circle closest-side at 50% 50%, #6A6A6A 0%, #848484 40%, #9A9A9A 75%, #777777 100%)'
export const CONE_COLOUR_GRADIENT =
  `radial-gradient(circle closest-side at 50% 50%, ${CONE_APEX} 0%, ${CONE_APEX} ${CONE_APEX_HOLD * 100}%, ${CONE_MID} ${CONE_MID_AT * 100}%, ${CONE_OUTER} ${CONE_OUTER_AT * 100}%)`

// The disc behind the cone. Colour: completely clear over the inner half of
// its radius, then building evenly to a 35% white at the rim (Gavin's mocks,
// 8 Oct; Kai the same day: "push the white out more"). Mono: clear only at
// the very centre, building to a 50% white at the rim, as his 16 Oct mock.
export const DISC_MONO_GRADIENT =
  'radial-gradient(circle closest-side at 50% 50%, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.15) 40%, rgba(255, 255, 255, 0.5) 100%)'
export const DISC_COLOUR_GRADIENT =
  'radial-gradient(circle closest-side at 50% 50%, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 0.35) 100%)'

// The cone's far end is curved — a true pie slice. Gavin's mock drew it flat,
// which is faithful at his 56° but thins to a sliver by 136° when the viewer
// zooms out, and a flat end cannot close up into the overhead circle. Kai
// chose the curve, 16 Sep.

// Captured when the module first loads, not when the compass mounts. The
// public page redirects an id to the video's canonical slug before the player
// appears, and that navigation drops the query string — so by the time
// anything asks, `?compass=v9` has already gone from the address bar.
const initialSearch = typeof window === 'undefined' ? '' : window.location.search

const queryParam = (name: string): string | null =>
  new URLSearchParams(initialSearch).get(name)

// A version key ('v9') or one of Gavin's file names, to a version key. Own
// properties only: `in` would also accept 'toString' and friends.
export const resolveCompassKey = (value: string | null): CompassVariantKey | undefined => {
  if (!value) return undefined
  if (Object.hasOwn(COMPASS_VARIANTS, value)) return value as CompassVariantKey
  const name = value.trim().toLowerCase()
  const file = name.endsWith('.png') ? name : `${name}.png`
  return Object.hasOwn(GAVIN_FILE_NAMES, file) ? GAVIN_FILE_NAMES[file] : undefined
}

export const compassVariant = (): CompassVariant =>
  COMPASS_VARIANTS[resolveCompassKey(queryParam('compass')) ?? DEFAULT_COMPASS]

export type RadarPaint = 'mono' | 'colour'

// Mono by default, since that is what Gavin asked to look at; ?radar=colour
// brings back the cyan-to-green cone to compare.
export const radarPaint = (): RadarPaint => {
  const v = queryParam('radar')?.trim().toLowerCase()
  return v === 'colour' || v === 'color' ? 'colour' : 'mono'
}

export const coneGradient = (): string =>
  radarPaint() === 'mono' ? CONE_MONO_GRADIENT : CONE_COLOUR_GRADIENT

export const discGradient = (): string =>
  radarPaint() === 'mono' ? DISC_MONO_GRADIENT : DISC_COLOUR_GRADIENT

// The verification button is a V badge. Gavin's 21 Oct drawing,
// verify-button-v5, is the default. Before it, 19 Oct, he sent two redrawings,
// both a V with a dot over its left arm: verify-button-v4-a (heavy strokes,
// ?verifylogo=v4-a) and verify-button-v4-b (light ones, ?verifylogo=v4-b).
// ?verifylogo=v3 the 17 Oct
// V in its rounded box. Before that it was the FileFidelity logo (16 Oct),
// kept as ?verifylogo=logo (his second drawing) and ?verifylogo=logo-v1 (his
// first). The logo is round with small holes for eyes, so it sits a touch
// larger than the player's other glyphs (`player/PlayerGlyph.tsx`) to keep
// them open.
export interface VerificationGlyphArt {
  image: string
  size: number
  /** The V heads the verification panel too; the logo variants keep the
   * full-colour logo there. */
  isLogo: boolean
}

export const verificationGlyph = (): VerificationGlyphArt => {
  switch (queryParam('verifylogo')?.trim().toLowerCase()) {
    case 'logo': return { image: '/img/button-verification.png', size: 24, isLogo: true }
    case 'logo-v1': return { image: '/img/button-verification-v1.png', size: 24, isLogo: true }
    case 'v3': return { image: '/img/verify-button-v3.png', size: 20, isLogo: false }
    case 'v4-a': return { image: '/img/verify-button-v4-a.png', size: 20, isLogo: false }
    case 'v4-b': return { image: '/img/verify-button-v4-b.png', size: 20, isLogo: false }
    default: return { image: '/img/verify-button-v5.png', size: 20, isLogo: false }
  }
}

export type ViewButtonVariant = 'menu' | 'v4' | 'v3'

// Split screen's View button, under each half's radar. 'menu' (Gavin, 22 Oct:
// "No Eye please... just the dropdown menu, in line with centre of radar") is
// a plain dropdown naming the half's view, centred under the radar.
// ?viewbutton=v4 brings back his 19 Oct eye — a black eye on a grey, lit disc
// (ViewEyeDisc.tsx); ?viewbutton=v3 the 11 Oct one, the bar's white eye on the
// radar's own disc.
export const viewButtonVariant = (): ViewButtonVariant => {
  const v = queryParam('viewbutton')?.trim().toLowerCase()
  return v === 'v3' || v === 'v4' ? v : 'menu'
}

// Fill by default: played in the gradient, loaded as a light band. Painting
// the whole bar (Gavin's swatch taken literally) left it static, with no sense
// of progress or of what had loaded — kept as ?playbar=track to compare.
export const playbarVariant = (): PlaybarVariant =>
  queryParam('playbar') === 'track' ? 'track' : 'fill'
